import { StopNew } from "../../../ducks/data/route-with-stops/types";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { loadStopDocuments } from "../../../ducks/data/stops-documents";
import { useAppDispatch } from "../../../redux-store";
import { useSelector } from "react-redux";
import { selectStopDocuments } from "../../../ducks/data/stops-documents/selectors";
import { Link as MUILink } from "@mui/material";
import { isMobile } from "../../../helpers/mobile";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import "./stop-component.scss";
import { CapacitorHttp } from "@capacitor/core";
import { apiFetch } from "../../../helpers/apiRequest";
import useFeature from "../../../hooks/useFeature";
import * as connectionActions from "../../../ducks/app/connection";
import { createDecoder } from "../../../ducks/api";
import { driverCmrConfigsT } from "dora-contracts";
import * as t from "io-ts";

const decodeCmrConfigs = createDecoder(driverCmrConfigsT);

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve((reader.result as string).split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const StopDocuments = ({ stop }: { stop: StopNew }) => {
  const { t } = useTranslation(["translation"]);
  const [loadingState, setLoadingState] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const dispatch = useAppDispatch();
  const cmrDownloadsEnabled = useFeature("mobile-app-cmr-documents-download");
  const manyToManyEnabled = useFeature("pickups-dropoffs-many-to-many");
  const [cmrConfigs, setCmrConfigs] = useState<
    t.TypeOf<typeof driverCmrConfigsT>
  >([]);

  const files = useSelector(selectStopDocuments(stop.id));

  useEffect(() => {
    setLoadingState("loading");
    // TODO: check this first
    dispatch(loadStopDocuments({ stopId: stop.id }))
      .then(() => {
        setLoadingState("idle");
      })
      .catch((err) => {
        console.error(err);
        setLoadingState("error");
      });
  }, [dispatch, stop]);

  useEffect(() => {
    if (!cmrDownloadsEnabled || !manyToManyEnabled) {
      return;
    }
    apiFetch(`/drivers-api/cmr-configs?stopId=${stop.id}`)
      .then(decodeCmrConfigs)
      .then((configs) => setCmrConfigs(configs))
      .catch((err) => {
        console.error("Error fetching CMR configs for stop", err);
      });
  }, [cmrDownloadsEnabled, manyToManyEnabled, stop.id]);

  if (!files.length && !cmrDownloadsEnabled) {
    return null;
  }

  const downloadFile = async (url: string, fileName: string) => {
    try {
      const signedRes = await apiFetch(url);
      const { url: signedUrl } = await signedRes.json();
      if (isMobile) {
        const httpResponse = await CapacitorHttp.request({
          method: "GET",
          url: signedUrl,
          responseType: "arraybuffer",
        });
        await Filesystem.writeFile({
          path: fileName,
          data: httpResponse.data as string,
          directory: Directory.Documents,
        });
        const fileUri = await Filesystem.getUri({
          directory: Directory.Documents,
          path: fileName,
        });
        await FileOpener.open({ filePath: fileUri.uri });
      } else {
        window.open(signedUrl, "_blank", "noreferrer");
      }
    } catch (error) {
      console.error("Error downloading file", error);
    }
  };

  const onCmrDocumentClick = async (cmrConfigId?: string) => {
    const url = cmrConfigId
      ? `/drivers-api/cmr-pdfs?cmrConfigId=${cmrConfigId}`
      : `/drivers-api/cmr-pdfs?stopId=${stop.id}`;
    const res = await apiFetch(url);
    if (res.status !== 200) {
      dispatch(connectionActions.errorReturned());
      throw new Error("Error fetching CMR PDFs");
    }
    const blob = await res.blob();

    if (isMobile) {
      const base64 = await blobToBase64(blob);
      const fileName = `cmr-${Date.now()}.pdf`;
      const saved = await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Cache,
      });
      await FileOpener.open({
        filePath: saved.uri,
        contentType: "application/pdf",
      });
    } else {
      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    }
  };

  return (
    <>
      <div className="stop-component">
        <AttachFileIcon className="stop-component-icon" />
        <div className="flex-grow">
          {cmrDownloadsEnabled && manyToManyEnabled
            ? cmrConfigs.map((cmrConfig) => (
                <div key={cmrConfig.id}>
                  <MUILink onClick={() => onCmrDocumentClick(cmrConfig.id)}>
                    {t("translation:CMRDocumentFromTo", {
                      from: cmrConfig.from,
                      to: cmrConfig.to || t("translation:CMRNoDropoff"),
                    })}
                  </MUILink>
                </div>
              ))
            : cmrDownloadsEnabled && (
                <MUILink onClick={() => onCmrDocumentClick()}>
                  {t("translation:CMRDocument")}
                </MUILink>
              )}
          {loadingState === "loading" && t("translation:LoadingFiles")}
          {loadingState === "error" && t("translation:ErrorLoadingFiles")}
          {loadingState === "idle" &&
            files.length > 0 &&
            files.map((file) => (
              <div key={file.id}>
                <MUILink
                  onClick={() => downloadFile(file.urlNew, file.name)}
                >
                  {file.name}
                </MUILink>
              </div>
            ))}
        </div>
      </div>
    </>
  );
};

export default StopDocuments;
